import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TabId } from './types';

interface Tab {
    id: TabId;
    label: string;
}

interface TaskConfigTabsProps {
    tabs: Tab[];
    activeTab: TabId;
    onTabChange: (tab: TabId) => void;
    validationErrors: Record<string, string>;
}

// Map tabs to their validation error prefixes
const tabErrorPrefixes: Record<TabId, string[]> = {
    capability: ['capability'],
    input: ['param.', 'inputPath'],
    output: ['resultPath', 'outputPath'],
    resilience: ['timeout', 'retry.'],
    errors: ['catch.'],
    advanced: ['comment'],
};

export function TaskConfigTabs({
    tabs,
    activeTab,
    onTabChange,
    validationErrors,
}: TaskConfigTabsProps) {
    // Check if a tab has validation errors
    const tabHasErrors = (tabId: TabId): boolean => {
        const prefixes = tabErrorPrefixes[tabId];
        return Object.keys(validationErrors).some((key) =>
            prefixes.some((prefix) => key.startsWith(prefix) || key === prefix)
        );
    };

    return (
        <div className="flex border-b border-slate-200 bg-white px-6">
            {tabs.map((tab) => {
                const hasErrors = tabHasErrors(tab.id);
                const isActive = activeTab === tab.id;

                return (
                    <button
                        key={tab.id}
                        onClick={() => onTabChange(tab.id)}
                        className={cn(
                            "relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px",
                            isActive
                                ? "border-blue-600 text-blue-600 bg-blue-50/50"
                                : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50",
                            hasErrors && !isActive && "text-red-500"
                        )}
                    >
                        {tab.label}
                        {hasErrors && (
                            <AlertCircle
                                size={14}
                                className={cn(
                                    isActive ? "text-red-500" : "text-red-400"
                                )}
                            />
                        )}
                    </button>
                );
            })}
        </div>
    );
}

